import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

export const Contact = () => {
  useScrollAnimations();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    serviceRequired: '',
    message: '',
    urgency: 'standard',
    attachments: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(files);
    setFormData({
      ...formData,
      attachments: files
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('propertyType', formData.propertyType);
      submitData.append('serviceRequired', formData.serviceRequired);
      submitData.append('message', formData.message);
      submitData.append('urgency', formData.urgency);
      
      // Append files
      uploadedFiles.forEach((file, index) => {
        submitData.append('attachments', file);
      });

      const response = await api.post('/contact', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Message Sent!', {
          description: "We'll get back to you as soon as possible.",
        });
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            propertyType: '',
            serviceRequired: '',
            message: '',
            urgency: 'standard',
            attachments: []
          });
          setUploadedFiles([]);
          setIsSubmitted(false);
          // Reset file input
          const fileInput = document.getElementById('file-upload');
          if (fileInput) fileInput.value = '';
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Submission Failed', {
        description: 'Please try again or call us directly at 07710 613783.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailClient = () => {
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Please fill in all required fields', {
        description: 'Name, email, phone, and message are required.',
      });
      return;
    }

    // Create mailto link with all form data
    const subject = encodeURIComponent(`Contact Form: ${formData.serviceRequired || 'General Enquiry'} - ${formData.urgency.toUpperCase()}`);
    
    const body = encodeURIComponent(`Hi Joe,

I'm contacting you regarding: ${formData.serviceRequired || 'General enquiry'}

URGENCY: ${formData.urgency.toUpperCase()}
Property Type: ${formData.propertyType || 'Not specified'}

CONTACT DETAILS:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}

MESSAGE:
${formData.message}

---
Sent from Royal Security website contact form`);

    const mailtoLink = `mailto:joe@royalsecurity.info?subject=${subject}&body=${body}`;
    
    // Open in new window/tab to avoid navigation issues
    window.open(mailtoLink, '_blank');
  };

  const contactInfo = [
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      content: '07710 613783',
      link: 'tel:07710613783',
      description: 'Call for immediate assistance'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'Joe@RoyalSecurity.info',
      link: 'mailto:Joe@RoyalSecurity.info',
      description: 'Send us your enquiry'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Service Area',
      content: 'UK Properties',
      description: 'Serving across the UK'
    },
    {
      icon: <Clock size={24} />,
      title: 'Availability',
      content: 'Emergency Call-Out',
      description: 'Available for urgent repairs'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            Get In Touch
          </h1>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            Contact us for a quote, to raise a job, or to discuss your property requirements.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '24px',
            marginBottom: '60px'
          }}>
            {contactInfo.map((info, index) => (
              <div key={index} style={{
                background: 'var(--royal-white)',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                e.currentTarget.style.borderColor = 'var(--royal-gold)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: 'var(--royal-navy)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: 'var(--royal-gold)'
                }}>
                  {info.icon}
                </div>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: 'var(--royal-navy)'
                }}>
                  {info.title}
                </h3>
                {info.link ? (
                  <a href={info.link} style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: 'var(--royal-gold)',
                    textDecoration: 'none',
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    {info.content}
                  </a>
                ) : (
                  <p style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: 'var(--royal-gold)',
                    marginBottom: '8px'
                  }}>
                    {info.content}
                  </p>
                )}
                <p style={{
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)',
                  margin: 0
                }}>
                  {info.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '60px',
            alignItems: 'start'
          }}>
            {/* Form */}
            <div style={{ maxWidth: '600px' }}>
              <h2 className="royal-heading-md" style={{ marginBottom: '16px' }}>
                Send Us a Message
              </h2>
              <p className="royal-body-lg" style={{ marginBottom: '32px' }}>
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    color: 'var(--royal-navy)'
                  }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      color: 'var(--royal-navy)'
                    }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      color: 'var(--royal-navy)'
                    }}>
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                      onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    color: 'var(--royal-navy)'
                  }}>
                    Property Type
                  </label>
                  <select
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                  >
                    <option value="">Select property type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="rental">Rental Property</option>
                    <option value="managed">Managed Property</option>
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    color: 'var(--royal-navy)'
                  }}>
                    Service Required
                  </label>
                  <select
                    name="serviceRequired"
                    value={formData.serviceRequired}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                  >
                    <option value="">Select service</option>
                    <option value="locksmith">Locksmith Services</option>
                    <option value="windows">Window Repairs</option>
                    <option value="doors">Door Repairs</option>
                    <option value="glazing">Glazing/Glass Replacement</option>
                    <option value="security">Security Upgrades</option>
                    <option value="emergency">Emergency Call-Out</option>
                    <option value="quote">General Quote Request</option>
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    color: 'var(--royal-navy)'
                  }}>
                    Urgency
                  </label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {['standard', 'urgent', 'emergency'].map((level) => (
                      <label key={level} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="radio"
                          name="urgency"
                          value={level}
                          checked={formData.urgency === level}
                          onChange={handleChange}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem', textTransform: 'capitalize' }}>
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    color: 'var(--royal-navy)'
                  }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      transition: 'border-color 0.3s ease',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                    placeholder="Please provide as much detail as possible including:
• Exact location/address and property type
• Measurements (door/window sizes, lock dimensions)
• Number of items requiring service
• Current status/condition and any issues observed
• Preferred availability (AM/PM, specific dates)
• Photos of the issue (you can attach below)
• Any other relevant information

The more details you provide, the faster and more accurate our quote will be!"
                  />
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    color: 'var(--royal-navy)'
                  }}>
                    Attach Photos/Documents (Optional)
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileChange}
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--royal-gold)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                  />
                  <p style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    marginTop: '8px'
                  }}>
                    Upload photos of the issue, measurements, or any relevant documents (Max 10MB per file)
                  </p>
                  {uploadedFiles.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--royal-navy)' }}>
                        Selected files ({uploadedFiles.length}):
                      </strong>
                      <ul style={{ 
                        marginTop: '8px', 
                        paddingLeft: '20px',
                        fontSize: '0.85rem',
                        color: 'var(--text-secondary)'
                      }}>
                        {uploadedFiles.map((file, index) => (
                          <li key={index}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || isSubmitted}
                  className="royal-btn-primary"
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    opacity: (isSubmitting || isSubmitted) ? 0.7 : 1,
                    cursor: (isSubmitting || isSubmitted) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle size={20} />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleEmailClient}
                  className="royal-btn-secondary"
                  style={{
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <Mail size={20} />
                  Open in Email App
                </button>
                
                <p style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                  marginTop: '12px'
                }}>
                  Having issues? Click "Open in Email App" to use your default email client.
                </p>
              </form>
            </div>

            {/* Info Sidebar */}
            <div>
              <div style={{
                background: 'var(--royal-navy)',
                padding: '48px',
                borderRadius: '12px',
                color: 'var(--royal-white)',
                marginBottom: '32px'
              }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '20px',
                  color: 'var(--royal-gold)'
                }}>
                  Need Immediate Help?
                </h3>
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.7',
                  marginBottom: '24px',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}>
                  For urgent repairs or emergency situations, call us directly for immediate assistance.
                </p>
                <a
                  href="tel:07710613783"
                  className="royal-btn-light"
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}
                >
                  <Phone size={20} />
                  Call 07710 613783
                </a>
                <a
                  href="mailto:Joe@RoyalSecurity.info"
                  className="royal-btn-secondary"
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'var(--royal-white)'
                  }}
                >
                  <Mail size={20} />
                  Email Us
                </a>
              </div>

              <div style={{
                background: 'var(--bg-section-alt)',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h3 style={{
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: 'var(--royal-navy)'
                }}>
                  What Happens Next?
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'We review your enquiry within 2 hours',
                    'Contact you to discuss requirements',
                    'Provide clear quote if needed',
                    'Schedule appointment at your convenience'
                  ].map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '12px',
                      fontSize: '0.95rem'
                    }}>
                      <CheckCircle size={18} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
